"use client";

/**
 * OsMock — animated mock of an askFinz OS device viewed from
 * `access.askfinz.ai`. Lives on /os.
 *
 * askFinz OS is a purpose-built Linux distro (Ubuntu Server 24.04 LTS +
 * k3s + KubeVirt) that turns a workstation, server or IoT device into a
 * dual-role managed appliance: a thin-client Chromium kiosk pointing at
 * `dash.askfinz.ai`, and a managed compute node where the cloud
 * orchestrates user workloads (containers, full VMs, distributed
 * training, clustered inference) onto the user's own hardware.
 *
 * The mock has to read as that — not as "your workspaces, signed in"
 * but as "your hardware, managed". A device-detail view: live hardware
 * readouts on the left, the kiosk frame in the middle, scheduled
 * workloads on the right, and a footer showing this device is one
 * node in a small LAN cluster.
 *
 * Loop: every ~8s a new workload lands on the device. The list shifts
 * up, hardware readouts move, then the loop replays.
 *
 * Pure presentation. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Cpu,
  Activity,
  HardDrive,
  Zap,
  Boxes,
  Layers,
  Server,
  Smartphone,
  Globe,
  Wifi,
  CheckCircle2,
  Power,
  Terminal,
  Container,
} from "lucide-react";

type Workload = {
  id: string;
  icon: typeof Container;
  label: string;
  detail: string;
  badge: string;
  tone: "running" | "training" | "vm" | "serving";
};

const WORKLOAD_POOL: Workload[] = [
  { id: "vllm",     icon: Zap,       label: "vllm-llama3-8b",      detail: "GPU 0 · 84% · 412 tok/s",          badge: "serving",  tone: "serving"  },
  { id: "train",    icon: Activity,  label: "pytorch-finetune",    detail: "epoch 14/50 · loss 0.182",          badge: "training", tone: "training" },
  { id: "deploy",   icon: Container, label: "marketing-api · v42", detail: "deployed from code.askfinz.ai",     badge: "running",  tone: "running"  },
  { id: "kubevirt", icon: Layers,    label: "win11-design · vm",   detail: "KubeVirt · GPU passthrough on",     badge: "vm",       tone: "vm"       },
  { id: "search",   icon: Globe,     label: "search-index-crawler", detail: "78k pages · 12 q/s",                badge: "running",  tone: "running"  },
  { id: "ray",      icon: Boxes,     label: "ray-head-0",           detail: "3 workers across LAN cluster",      badge: "running",  tone: "running"  },
];

const PEERS = [
  { id: "server",  icon: Server,     label: "gpu-server-01", spec: "2× RTX 4090 · 128 GB",  state: "ready"    },
  { id: "iot",     icon: Smartphone, label: "rk3588-edge",   spec: "ARM64 · 16 GB · NPU",   state: "ready"    },
  { id: "self",    icon: Power,      label: "this device",   spec: "RTX 4070 · 64 GB",      state: "active"   },
];

const BADGE_STYLES: Record<Workload["tone"], string> = {
  running:  "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  training: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  vm:       "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
  serving:  "bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.30)]",
};

export function OsMock() {
  // Show 4 workloads at a time, rotating through the pool.
  const [offset, setOffset] = useState(0);
  // Live-ish hardware readouts that wiggle on each cycle.
  const [tick, setTick] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const step = () => {
      setOffset((o) => (o + 1) % WORKLOAD_POOL.length);
      setTick((t) => t + 1);
      schedule(step, 4200);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(step, 2000);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  const visible: Workload[] = Array.from({ length: 4 }, (_, i) => {
    return WORKLOAD_POOL[(offset + i) % WORKLOAD_POOL.length];
  });

  // Deterministic-ish wiggle for hardware readouts so the SSR + client
  // first paint match, then values shift per tick.
  const cpu  = 24 + ((tick * 7) % 22);
  const gpu  = 62 + ((tick * 11) % 28);
  const ram  = 38 + ((tick * 5) % 14);
  const temp = 54 + ((tick * 3) % 12);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[14px] border hairline-strong bg-card/85 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Top status bar — access.askfinz.ai device-detail view */}
        <div className="relative flex items-center px-3 h-10 border-b hairline bg-foreground/[0.04] text-[10.5px] font-mono tracking-[0.04em] text-foreground/65">
          <div className="inline-flex items-center gap-1.5">
            <Power className="h-3 w-3 text-[hsl(var(--accent))]" />
            <span>access.askfinz.ai</span>
            <span className="text-foreground/35">·</span>
            <span>devices / paul-workstation</span>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 hidden md:inline-flex items-center gap-2">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>workstation · linked · uptime 14d 06:42</span>
          </div>
          <div className="ml-auto inline-flex items-center gap-2.5">
            <Wifi className="h-3 w-3" />
            <span>192.168.1.42</span>
            <span className="text-foreground/35">·</span>
            <span>k3s-server</span>
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[480px] md:min-h-[540px]">
          {/* Left rail — live hardware */}
          <aside className="col-span-12 md:col-span-3 border-r hairline bg-foreground/[0.03] p-3">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Hardware · live
            </div>
            <div className="space-y-1.5">
              <HardwareRow icon={Cpu}      label="CPU"      value={`${cpu}%`}      sub="Ryzen 9 7950X · 16c/32t" pct={cpu} />
              <HardwareRow icon={Zap}      label="GPU 0"    value={`${gpu}%`}      sub="RTX 4070 · NVIDIA"       pct={gpu} tone="accent" />
              <HardwareRow icon={Activity} label="GPU temp" value={`${temp}°C`}    sub="fan 47% · 28 dB"         pct={(temp - 30) * 2} />
              <HardwareRow icon={HardDrive}label="RAM"      value={`${ram}/64 GB`} sub="DDR5-6000"               pct={(ram / 64) * 100} />
            </div>

            <div className="mt-4 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Device plugins
            </div>
            <ul className="text-[10.5px] space-y-1">
              <li className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                <span className="text-foreground/75">nvidia.com/gpu</span>
                <span className="ml-auto font-mono text-foreground/65">1</span>
              </li>
              <li className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                <span className="text-foreground/75">sriov-net</span>
                <span className="ml-auto font-mono text-foreground/65">8</span>
              </li>
              <li className="flex items-center gap-1.5 opacity-60">
                <span className="h-1.5 w-1.5 rounded-full bg-foreground/30 ml-[3px]" />
                <span className="text-foreground/65">coral.ai/tpu</span>
                <span className="ml-auto font-mono text-foreground/65">—</span>
              </li>
            </ul>
          </aside>

          {/* Center — kiosk frame */}
          <main className="col-span-12 md:col-span-5 relative flex flex-col bg-background overflow-hidden">
            <div className="px-3 py-2 border-b hairline flex items-center gap-2 bg-foreground/[0.02]">
              <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
              <span className="ml-2 inline-flex items-center gap-1.5 rounded-md border hairline bg-background px-2 py-0.5 text-[10px] font-mono text-foreground/65">
                <Globe className="h-3 w-3" />
                https://dash.askfinz.ai
              </span>
              <span className="ml-auto text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                kiosk
              </span>
            </div>

            <div className="relative flex-1 flex flex-col items-center justify-center p-5 text-center">
              <div aria-hidden className="absolute inset-0 grid-bg-dense opacity-[0.10]" />
              <div className="relative inline-flex items-center gap-1.5 rounded-full border hairline px-2.5 py-0.5 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                signed in · paul@northvine.example
              </div>
              <div className="relative mt-4 display-serif text-2xl md:text-3xl leading-tight">
                Good morning.
              </div>
              <p className="relative mt-2 text-[12px] text-foreground/65 max-w-xs leading-relaxed">
                The kiosk opens straight into the cloud — same workshop you get
                in any browser, served fullscreen on the device.
              </p>

              <div className="relative mt-5 grid grid-cols-3 gap-1.5 w-full max-w-xs">
                {["Chat", "Research", "Code", "News", "Mail", "Storage"].map((w) => (
                  <div
                    key={w}
                    className="rounded-md border hairline bg-background/85 p-2 text-[10.5px] text-foreground/75"
                  >
                    {w}
                  </div>
                ))}
              </div>

              <div className="relative mt-auto pt-4 text-[9.5px] font-mono tracking-[0.16em] uppercase text-foreground/65">
                cloud-rendered · device contributes compute
              </div>
            </div>
          </main>

          {/* Right rail — scheduled workloads */}
          <aside className="col-span-12 md:col-span-4 border-l hairline bg-foreground/[0.02] p-3 flex flex-col">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                Scheduled workloads
              </span>
              <span className="text-[9.5px] font-mono text-foreground/65">k3s · KubeVirt</span>
            </div>

            <div className="space-y-1.5 flex-1">
              {visible.map((w, i) => {
                const Icon = w.icon;
                return (
                  <div
                    key={`${w.id}-${tick}-${i}`}
                    className="rounded-md border hairline bg-background/65 p-2.5"
                    style={{ animation: "blur-fade-in 0.45s ease-out both", animationDelay: `${i * 70}ms` }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border hairline">
                        <Icon className="h-3 w-3" />
                      </span>
                      <span className="text-[11.5px] font-medium text-foreground/85 truncate">
                        {w.label}
                      </span>
                      <span
                        className={
                          "ml-auto inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em] " +
                          BADGE_STYLES[w.tone]
                        }
                      >
                        {w.badge}
                      </span>
                    </div>
                    <div className="mt-1 pl-8 text-[10.5px] font-mono text-foreground/65 truncate">
                      {w.detail}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 border-t hairline pt-2.5">
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-1.5">
                LAN cluster · 3 nodes
              </div>
              <ul className="space-y-1 text-[10.5px]">
                {PEERS.map((p) => {
                  const Icon = p.icon;
                  const isActive = p.state === "active";
                  return (
                    <li key={p.id} className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 text-foreground/65" />
                      <span className={isActive ? "text-foreground/85 font-medium" : "text-foreground/65"}>
                        {p.label}
                      </span>
                      <span className="text-foreground/65 truncate hidden sm:inline">· {p.spec}</span>
                      <span
                        className={
                          "ml-auto inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-[0.14em] " +
                          (isActive ? "text-[hsl(var(--accent))]" : "text-foreground/65")
                        }
                      >
                        <span className={"h-1.5 w-1.5 rounded-full " + (isActive ? "bg-[hsl(var(--accent))]" : "bg-emerald-500")} />
                        {p.state}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-3 inline-flex items-center gap-1.5 rounded-md border hairline bg-background/60 px-2 py-1.5 text-[10px] font-mono text-foreground/65">
              <Terminal className="h-3 w-3" />
              ssh paul@paul-workstation (cert · 1h)
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function HardwareRow({
  icon: Icon,
  label,
  value,
  sub,
  pct,
  tone,
}: {
  icon: typeof Cpu;
  label: string;
  value: string;
  sub: string;
  pct: number;
  tone?: "accent";
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="rounded-md border hairline bg-background/65 px-2.5 py-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-foreground/65" />
        <span className="text-[11px] font-medium text-foreground/85">{label}</span>
        <span className="ml-auto text-[10.5px] font-mono text-foreground/75">{value}</span>
      </div>
      <div className="mt-1 h-1 w-full rounded-full bg-foreground/10 overflow-hidden">
        <div
          className={
            "h-full transition-[width] duration-700 ease-out " +
            (tone === "accent" ? "bg-[hsl(var(--accent))]" : "bg-foreground/55")
          }
          style={{ width: `${clamped}%` }}
        />
      </div>
      <div className="mt-1 text-[9.5px] font-mono text-foreground/65">{sub}</div>
    </div>
  );
}
